<template>
	<div>
		<div class="container hidden-xs">
			<b-breadcrumbs :items="breadcrumbs"/>
		</div>
		<section class="container">
			<div class="row">
				<div class="col-xs-12">
					<h1>New user</h1>
					<div class="page-summary-text">
						Add a new user
					</div>
				</div>
			</div>
			<div class="row">
				<div class="col-xs-12 col-sm-6">
					<h2>Details</h2>
					<label for="title">Title</label>
					<input
						v-model="user.title"
						type="text"
						name="title"
						class="form-control"
						placeholder="Title"
					/>
					<label for="firstName">First name</label>
					<label
						v-if="$v.user.first_name.$error && !$v.user.first_name.required"
						class="label error pull-right"
					>Required</label>
					<input
						v-model="user.first_name"
						:class="{ 'error': $v.user.first_name.$error }"
						type="text"
						name="firstName"
						class="form-control"
						placeholder="First name"
					/>
					<label for="lastName">Last name</label>
					<label
						v-if="$v.user.last_name.$error && !$v.user.last_name.required"
						class="label error pull-right"
					>Required</label>
					<input
						v-model="user.last_name"
						:class="{ 'error': $v.user.last_name.$error }"
						type="text"
						name="lastName"
						class="form-control"
						placeholder="Last name"
					/>
					<label for="email">Email address</label>
					<label
						v-if="$v.user.email_address.$error && !$v.user.email_address.required"
						class="label error pull-right"
					>Required</label>
					<input
						v-model="user.email_address"
						:class="{ 'error': $v.user.email_address.$error }"
						type="email"
						name="email"
						class="form-control"
						placeholder="Email address"
					/>
				</div>
				<div class="col-xs-12 col-sm-6">
					<h2>Attributes</h2>
					<div class="row">
						<div class="col-xs-12">
							<label for="admin">User level</label>
							<div class="switch">
								<span class="toggle-switch">
									<input v-model="user.is_admin" id="isUser" name="isUser" type="checkbox">
									<label class="label-primary" for="isUser"/>
								</span>
								<label for="isUser" style="font-weight:normal;cursor:pointer;">
									{{ user.is_admin ? 'Admin' : 'User only' }}
								</label>
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col-xs-12">
							<label for="password">Password</label>
							<label
								v-if="$v.user.password.$error && !$v.user.password.required"
								class="label error pull-right"
							>Required</label>
							<label
								v-if="!$v.user.repeat_password.sameAs && $v.user.repeat_password.$error"
								class="label error pull-right"
							>Passwords must match</label>
							<input
								v-model="user.password"
								:class="{ 'error': $v.user.password.$error }"
								type="password"
								name="password"
								class="form-control"
								placeholder="Password (Must contain at least 6 characters)"
							/>
						</div>
					</div>
					<div class="row">
						<div class="col-xs-12">
							<label for="repeatPassword">Repeat password</label>
							<label
								v-if="!$v.user.repeat_password.required && $v.user.repeat_password.$error"
								class="label error pull-right"
							>Required</label>
							<label
								v-if="!$v.user.repeat_password.sameAs && $v.user.repeat_password.$error"
								class="label error pull-right"
							>Passwords must match</label>
							<input
								v-model="user.repeat_password"
								:class="{ 'error': $v.user.repeat_password.$error }"
								type="password"
								name="repeatPassword"
								class="form-control"
								placeholder="Repeat password"
							/>
						</div>
					</div>
					<!--
					<div class="row">
						<div class="col-xs-12">
							<label for="regions">Regions</label>
							<b-multi-select
								v-model="user.regions"
								:options="regions"
								name="regions"
								class="multi-select"
								filterable
								block
								collapse-selected
							/>
						</div>
					</div>
					-->
				</div>
			</div>
			<hr>
			<div class="row">
				<div class="col-xs-12">
					<div class="container center">
						<router-link to="/admin/users">
							<b-btn class="btn-md btn-default">Cancel</b-btn>
						</router-link>
						&nbsp;&nbsp;&nbsp;
						<b-btn class="btn-md btn-primary" @click="saveUser">Save</b-btn>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<style lang="less" scoped>
	.form-control {
		margin-bottom: 15px;
		border-radius: 4px !important;
	}
	.switch {
		margin-bottom: 15px;
	}
	.toggle-switch {
		margin-right: 5px;
	}
	.multi-select {
		margin-bottom: 15px;
	}
	.error {
		border-color: red;
		color: red;
	}
</style>

<script>
import { required, sameAs, minLength } from 'vuelidate/lib/validators';
import { map } from 'lodash/fp';
import { Notification } from 'uiv';
import { mapGetters } from 'vuex';

export default {
	created() {
		this.getRegions();
	},
	data() {
		return {
			regions: [],
			user: {
				title: null,
				first_name: null,
				last_name: null,
				is_admin: false,
				email_address: null,
				password: null,
				repeat_password: null,
				regions: []
			}
		};
	},
	computed: {
		...mapGetters({ getBreadcrumbs: 'breadcrumbs' }),
		breadcrumbs() {
			return this.getBreadcrumbs;
		}
	},
	validations: {
		user: {
			first_name: { required },
			last_name: { required },
			email_address: { required },
			password: { required, minLength: minLength(6) },
			repeat_password: { required, sameAsPassword: sameAs('password') }
		}
	},
	methods: {
		async getRegions() {
			const { data: { data } } = await this.$axios.get('/region');
			const regions = map(({ id, name }) => ({ value: id, label: name }), data);
			this.$set(this, 'regions', regions);
		},
		async saveUser() {
			if (this.$v.user.$invalid) {
				this.$v.user.$touch();
			} else {
				try {
					await this.$axios.post('/user', this.user);
					this.$router.push({ path: '/admin/users' });
					Notification.notify({
						type: 'success',
						title: 'User created successfully.',
						duration: 10000
					});
				} catch (e) {
					Notification.notify({
						type: 'danger',
						title: 'Could not create user.',
						content: e.response ? e.response.data.message : '',
						duration: 10000
					});
				}
			}
		}
	}
};
</script>
